'use client';
import { Box, keyframes } from '@mui/material';
import type { LottiePlayer } from 'lottie-web';
import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants';
import { MaterialIcon } from '../materialIcon';
import { MaterialIconType } from '../materialIcon/types';

export type StatusLottieType = 'success' | 'error' | 'validating' | 'loading';

// Same pulse as the badge in loading.json: scale 96% to 104% and back every 96 frames at 60fps
const loadingPulse = keyframes`
  from { transform: scale(0.96); }
  to { transform: scale(1.04); }
`;

// Same animations as customer-mobile's StatusLottie and Loading. The 377x701 canvas only uses a badge in the
// middle, so the view is cropped to a square around it; the success confetti still flies past the crop
// (overflow visible). validating is a wait animation, so it loops instead of stopping on its last frame
// Every badge has the same 110px rings, so one crop keeps them the same size on screen
const CROP = 160;

const ANIMATIONS: Record<
  StatusLottieType,
  { center: { x: number; y: number }; loop: boolean; load: () => Promise<{ default: unknown }> }
> = {
  success: {
    center: { x: 188, y: 320 },
    loop: false,
    load: () => import('../../assets/lotties/success.json'),
  },
  error: { center: { x: 188, y: 350 }, loop: false, load: () => import('../../assets/lotties/error.json') },
  validating: {
    center: { x: 189, y: 290 },
    loop: true,
    load: () => import('../../assets/lotties/validating.json'),
  },
  // customer-mobile's default Loading, with its shopping bag glyph removed: the icon is drawn on top instead
  loading: {
    center: { x: 189, y: 290 },
    loop: true,
    load: () => import('../../assets/lotties/loading.json'),
  },
};

/**
 * Animated success, error or validating badge from the mobile app. Decorative: the screen's text already says what happened,
 * so screen readers skip it, and with reduce motion on it shows its final frame instead of playing.
 *
 * @param {StatusLottieType} type - Which animation to play.
 * @param {number} [size=176] - Rendered width and height in px; the badge takes about 70% of it.
 * @param {MaterialIconType} [icon='hourglass_top'] - Glyph in the middle of the 'loading' badge; other types ignore it.
 * @return {React.JSX.Element} The rendered animation.
 */
export const StatusLottie = ({
  type,
  size = 176,
  icon = 'hourglass_top',
}: {
  type: StatusLottieType;
  size?: number;
  icon?: MaterialIconType;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The icon shows and starts pulsing with the badge once the animation is on screen, so both stay in step
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let destroyed = false;
    let destroy: (() => void) | undefined;
    // Player and JSON load lazily so pages without the animation don't ship them
    Promise.all([import('lottie-web/build/player/lottie_light.js'), ANIMATIONS[type].load()])
      .then(([player, { default: animationData }]) => {
        if (destroyed || !containerRef.current) return;
        // CommonJS build: bundlers hand the player as default, NodeNext type-checking wraps it once more
        const { default: lottieExport } = player as unknown as { default: LottiePlayer & { default?: LottiePlayer } };
        const lottie = lottieExport.default ?? lottieExport;
        const {
          center: { x, y },
          loop,
        } = ANIMATIONS[type];
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay: !reduceMotion,
          animationData,
          rendererSettings: { viewBoxSize: `${x - CROP / 2} ${y - CROP / 2} ${CROP} ${CROP}` },
        });
        animation.addEventListener('DOMLoaded', () => {
          if (reduceMotion) animation.goToAndStop(animation.totalFrames - 1, true);
          setLoaded(true);
        });
        destroy = () => animation.destroy();
      })
      .catch(() => {});
    return () => {
      destroyed = true;
      setLoaded(false);
      destroy?.();
    };
  }, [type]);

  return (
    <Box
      aria-hidden="true"
      data-testid={`status-lottie-${type}`}
      sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      <Box ref={containerRef} sx={{ width: '100%', height: '100%', '& svg': { overflow: 'visible' } }} />
      {type === 'loading' && loaded && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'scale(0.96)',
            animation: `${loadingPulse} 0.8s cubic-bezier(0.65, 0, 0.35, 1) infinite alternate`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {/* Sized like the mobile glyph: about 19% of the cropped view */}
          <MaterialIcon icon={icon} size={Math.round(size * 0.19)} sx={{ color: COLORS.brand.main }} />
        </Box>
      )}
    </Box>
  );
};
